import { useState, useEffect, useCallback } from 'react';
import { fetchAlerts, markAlertRead, markAllAlertsRead, deleteAlert, fetchAlertPreferences, updateAlertPreference } from '@/api';
import { LoadingPage } from '@/components/common/Loading';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Bell, BellOff, Check, CheckCheck, Trash2, TrendingUp, Zap, Target, ChevronLeft, ChevronRight, Settings } from 'lucide-react';
import { toast } from 'sonner';

const TYPE_CONFIG = {
  odds_change: { label: 'Odds Change', icon: TrendingUp, color: 'text-blue-400 bg-blue-500/10 border-blue-500/20' },
  value_bet:   { label: 'Value Bet',   icon: Target,     color: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20' },
  surebet:     { label: 'Surebet',     icon: Zap,        color: 'text-amber-400 bg-amber-500/10 border-amber-500/20' },
  new_match:   { label: 'New Match',   icon: Bell,       color: 'text-purple-400 bg-purple-500/10 border-purple-500/20' },
};

export default function AlertsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [filterRead, setFilterRead] = useState('unread');
  const [filterSeverity, setFilterSeverity] = useState('critical,high');
  const [page, setPage] = useState(1);
  const [showPrefs, setShowPrefs] = useState(false);
  const [prefs, setPrefs] = useState([]);

  const load = useCallback(() => {
    setLoading(true);
    const params = { page, limit: 30 };
    if (filterType !== 'all') params.alert_type = filterType;
    if (filterRead === 'unread') params.is_read = false;
    if (filterRead === 'read') params.is_read = true;
    if (filterSeverity !== 'all') params.severity = filterSeverity;
    fetchAlerts(params)
      .then(res => { setData(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [page, filterType, filterRead, filterSeverity]);

  useEffect(() => { load(); }, [load]);

  const loadPrefs = async () => {
    try {
      const res = await fetchAlertPreferences();
      setPrefs(res.data.preferences);
    } catch { /* ignore */ }
  };

  const handleMarkRead = async (id) => {
    await markAlertRead(id);
    load();
  };

  const handleMarkAllRead = async () => {
    await markAllAlertsRead();
    toast.success('All alerts marked as read');
    load();
  };

  const handleDelete = async (id) => {
    await deleteAlert(id);
    toast.success('Alert deleted');
    load();
  };

  const handleTogglePref = async (alertType, enabled) => {
    try {
      await updateAlertPreference({ alert_type: alertType, is_enabled: !enabled });
      toast.success(`${alertType} alerts ${!enabled ? 'enabled' : 'disabled'}`);
      loadPrefs();
    } catch { toast.error('Update failed'); }
  };

  return (
    <div data-testid="alerts-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight uppercase text-slate-50" style={{ fontFamily: 'var(--font-heading)' }}>
            <Bell className="w-5 h-5 inline mr-2 text-amber-400" />
            Alerts
          </h1>
          {data?.stats && (
            <div className="flex items-center gap-2 mt-2">
              <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20 text-xs">
                Unread: {data.stats.unread}
              </Badge>
              <Badge className="bg-slate-700/50 text-slate-300 border-slate-600 text-xs">
                Total: {data.stats.total}
              </Badge>
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={() => { setShowPrefs(!showPrefs); if (!showPrefs) loadPrefs(); }}
            className="bg-slate-800 text-slate-300 hover:bg-slate-700 border border-slate-700 h-8 text-xs"
            data-testid="toggle-prefs">
            <Settings className="w-3.5 h-3.5 mr-1" /> Preferences
          </Button>
          {data?.stats?.unread > 0 && (
            <Button size="sm" onClick={handleMarkAllRead}
              className="bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 border border-emerald-500/20 h-8 text-xs"
              data-testid="mark-all-read">
              <CheckCheck className="w-3.5 h-3.5 mr-1" /> Mark All Read
            </Button>
          )}
        </div>
      </div>

      {/* Preferences Panel */}
      {showPrefs && (
        <div className="mb-4 p-4 bg-slate-900/80 border border-slate-700 rounded-lg" data-testid="prefs-panel">
          <p className="text-sm font-medium text-slate-300 mb-3">Alert Preferences</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {prefs.map(p => {
              const cfg = TYPE_CONFIG[p.alert_type] || TYPE_CONFIG.odds_change;
              const Icon = cfg.icon;
              return (
                <button key={p.alert_type} onClick={() => handleTogglePref(p.alert_type, p.is_enabled)}
                  className={`flex items-center gap-2 p-3 rounded-lg border transition-colors ${
                    p.is_enabled ? cfg.color : 'bg-slate-800/50 border-slate-700 text-slate-500'
                  }`}
                  data-testid={`pref-${p.alert_type}`}>
                  <Icon className="w-4 h-4" />
                  <span className="text-xs font-medium">{cfg.label}</span>
                  <Badge className={`ml-auto text-[10px] ${p.is_enabled ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-700 text-slate-500'}`}>
                    {p.is_enabled ? 'ON' : 'OFF'}
                  </Badge>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-2 mb-4">
        <Select value={filterType} onValueChange={v => { setFilterType(v); setPage(1); }}>
          <SelectTrigger className="w-[140px] bg-slate-900 border-slate-700 text-sm h-9" data-testid="type-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-700">
            <SelectItem value="all">All Types</SelectItem>
            {Object.entries(TYPE_CONFIG).map(([key, cfg]) => (
              <SelectItem key={key} value={key}>{cfg.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterRead} onValueChange={v => { setFilterRead(v); setPage(1); }}>
          <SelectTrigger className="w-[120px] bg-slate-900 border-slate-700 text-sm h-9" data-testid="read-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-700">
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="unread">Unread</SelectItem>
            <SelectItem value="read">Read</SelectItem>
          </SelectContent>
        </Select>

        <Select value={filterSeverity} onValueChange={v => { setFilterSeverity(v); setPage(1); }}>
          <SelectTrigger className="w-[150px] bg-slate-900 border-slate-700 text-sm h-9" data-testid="severity-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-700">
            <SelectItem value="critical,high">Critical & High</SelectItem>
            <SelectItem value="all">All Severities</SelectItem>
            <SelectItem value="critical">Critical Only</SelectItem>
            <SelectItem value="high">High Only</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? <LoadingPage /> : (
        <>
          <div className="space-y-2" data-testid="alerts-list">
            {data?.alerts?.length > 0 ? data.alerts.map(alert => {
              const cfg = TYPE_CONFIG[alert.alert_type] || TYPE_CONFIG.odds_change;
              const Icon = cfg.icon;
              return (
                <div key={alert.id} className={`flex items-start gap-3 p-4 rounded-lg border transition-colors ${
                  alert.is_read ? 'bg-slate-900/30 border-slate-800/50' : 'bg-slate-900/70 border-slate-700'
                }`} data-testid={`alert-${alert.id}`}>
                  <div className={`p-2 rounded-lg shrink-0 ${cfg.color}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className={`text-sm font-medium ${alert.is_read ? 'text-slate-400' : 'text-slate-100'}`}>{alert.title}</p>
                      {!alert.is_read && <div className="w-2 h-2 rounded-full bg-amber-400 shrink-0" />}
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">{alert.message}</p>
                    <p className="text-[10px] text-slate-600 mt-1">{new Date(alert.created_at).toLocaleString()}</p>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    {!alert.is_read && (
                      <Button size="sm" variant="ghost" onClick={() => handleMarkRead(alert.id)}
                        className="text-slate-500 hover:text-emerald-400 h-7 w-7 p-0">
                        <Check className="w-3.5 h-3.5" />
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(alert.id)}
                      className="text-slate-500 hover:text-red-400 h-7 w-7 p-0">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              );
            }) : (
              <div className="text-center py-12 text-slate-500">
                <BellOff className="w-8 h-8 mx-auto mb-2 text-slate-600" />
                <p className="text-sm">No alerts yet</p>
              </div>
            )}
          </div>

          {data?.pages > 1 && (
            <div className="flex items-center justify-between mt-4 text-xs text-slate-500">
              <span>Page {data.page} of {data.pages} ({data.total} total)</span>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)}
                  className="h-7 border-slate-700 text-slate-400"><ChevronLeft className="w-3 h-3" /></Button>
                <Button size="sm" variant="outline" disabled={page >= data.pages} onClick={() => setPage(p => p + 1)}
                  className="h-7 border-slate-700 text-slate-400"><ChevronRight className="w-3 h-3" /></Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
